import React, { useState, useEffect, useRef } from 'react';
import AddressAutocomplete from '../components/Common/AddressAutocomplete';
import { FaFolderPlus, FaEdit, FaTrash, FaExclamationCircle } from 'react-icons/fa';
import './Drawer.css';
import ConfirmTooltip from '../components/Common/ConfirmTooltip';

const assignedToOptions = (process.env.REACT_APP_USER_EMAILS || '').split(',').map(e => e.trim()).filter(Boolean);
const statusOptions = ['Open', 'Pending', 'Review', 'Draft Sent', 'eSigned', 'Payment Pending', 'Payment Received', 'Completed'];

const FORM_SECTIONS = [
  { number: 1, title: 'Contact Info' },
  { number: 2, title: 'Status & Dates' },
  { number: 3, title: 'Filing & Invoice' },
  { number: 4, title: 'Notes & History' },
];

const initialCustomerState = {
  name: '',
  spouse: '',
  address: '',
  city: '',
  mobile: '',
  email: '',
  invoiceDate: '',
  filingDate: '',
  draftSentDate: '',
  invoiceNo: '',
  invoiceAmount: '',
  paymentReceived: '',
  dueDate: '',
  notes: '',
  familyDetails: '',
  history: '',
  status: 'Pending',
  assignedTo: '',
  otherComments: '',
  draftStatus: '',
  receivedDate: '',
  dob: '',
  workStatus: '',
};

const CustomerDrawer = ({ isOpen, onClose, customer, onSave, onDelete, saving, isAdmin, isDialerOpen, error }) => {

  const [formData, setFormData] = useState(initialCustomerState);
  const [activeSection, setActiveSection] = useState(1);
  const sectionRefs = useRef({});
  const drawerBodyRef = useRef(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const root = drawerBodyRef.current;
    if (!root) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(Number(entry.target.dataset.section));
          }
        });
      },
      { root, threshold: 0.1, rootMargin: '0px 0px -75% 0px' }
    );

    Object.values(sectionRefs.current).forEach((el) => el && observer.observe(el));
    return () => {
      observer.disconnect();
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && customer) {
      // Editing existing customer, pre-fill form
      setFormData({
        name: customer.name || '',
        spouse: customer.spouse || '',
        address: customer.address || '',
        city: customer.city || '',
        mobile: customer.mobile || '',
        email: customer.email || '',
        invoiceDate: customer.invoiceDate || '',
        invoiceNo: customer.invoiceNo || '',
        invoiceAmount: customer.invoiceAmount || '',
        paymentReceived: customer.paymentReceived || '',
        filingDate: customer.filingDate || '',
        draftSentDate: customer.draftSentDate || '',
        dueDate: customer.dueDate || '',
        status: customer.status || 'Pending',
        notes: customer.notes || '',
        dob: customer.dob || '',
        workStatus: customer.workStatus || '',
        assignedTo: customer.assignedTo || '',
        otherComments: customer.otherComments || '',
        draftStatus: customer.draftStatus || '',
        receivedDate: customer.receivedDate || '',
        familyDetails: customer.familyDetails || '',
        history: customer.history || '',
      });
    } else {
      // Adding new customer or closing, reset to initial state
      setFormData(initialCustomerState);
    }
  }, [isOpen, customer]);

  const scrollToSection = (number) => {
    sectionRefs.current[number]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddressChange = (value) => {
    setFormData(prev => ({ ...prev, address: value }));
  };

  const handlePlaceSelected = (place) => {
    setFormData(prev => ({ ...prev, address: place.formatted_address, city: place.city }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const isEditing = !!customer;

  return (
    <>
      <div className={`drawer-backdrop ${isOpen ? 'show' : ''}`}></div>
      <div className={`drawer-container ${isOpen ? 'show' : ''} ${isOpen && isDialerOpen ? 'shifted-for-dialer' : ''}`}>
        <div className="drawer-header">
          <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
            <div className="rounded-4 d-flex align-items-center justify-content-center bg-primary-subtle text-primary flex-shrink-0" style={{ width: '44px', height: '44px' }}>
              {isEditing ? <FaEdit size={18} /> : <FaFolderPlus size={18} />}
            </div>
            <div style={{ minWidth: 0 }}>
              <h4 className="mb-0 fw-bold text-dark text-truncate">{isEditing ? 'Edit Customer' : 'Add New Customer'}</h4>
              <small className="text-muted text-truncate d-block">{isEditing ? `Updating record for ${customer?.name || ''}` : 'Create a new personal tax client'}</small>
            </div>
          </div>
          <button type="button" className="btn-close ms-2 flex-shrink-0" onClick={onClose} aria-label="Close"></button>
        </div>

        {error && (
          <div className="alert alert-danger mx-3 mt-3 mb-0 border-0 rounded-3 small fw-semibold d-flex align-items-center gap-2 shadow-sm" style={{ backgroundColor: '#fee2e2', color: '#dc2626' }}>
            <FaExclamationCircle size={14} className="flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="ledger-tab-bar">
          {FORM_SECTIONS.map(s => (
            <button
              key={s.number}
              type="button"
              className={`ledger-tab-pill ${activeSection === s.number ? 'active' : ''}`}
              onClick={() => scrollToSection(s.number)}
            >
              <span>{s.number}</span>
              <span>{s.title}</span>
            </button>
          ))}
        </div>

        <div className="d-flex flex-grow-1 overflow-hidden">
          <form id="personal-customer-form" onSubmit={handleSubmit} className="d-flex flex-column flex-grow-1" style={{ overflow: 'hidden' }}>
            <div ref={drawerBodyRef} className="drawer-body">
              <div className="row g-3">
                <h4 ref={el => sectionRefs.current[1] = el} data-section={1} className="form-section-title"><span className="section-number">1</span> Contact Information</h4>
                <div className="col-md-6"><label className="form-label">Name</label><input type="text" name="name" value={formData.name} onChange={handleChange} className="form-control" required /></div>
                <div className="col-md-6"><label className="form-label">Spouse</label><input type="text" name="spouse" value={formData.spouse} onChange={handleChange} className="form-control" /></div>
                <div className="col-md-6"><label className="form-label">Mobile</label><input type="tel" name="mobile" value={formData.mobile} onChange={handleChange} className="form-control" required /></div>
                <div className="col-md-6"><label className="form-label">Email</label><input type="email" name="email" value={formData.email} onChange={handleChange} className="form-control" /></div>
                <div className="col-md-6"><label className="form-label">Address</label><AddressAutocomplete className="form-control" value={formData.address} onChange={(e) => handleAddressChange(e.target.value)} onPlaceSelected={handlePlaceSelected} /></div>
                <div className="col-md-6"><label className="form-label">City</label><input type="text" name="city" value={formData.city} onChange={handleChange} className="form-control" /></div>
                <div className="col-md-6"><label className="form-label">Date of Birth</label><input type="date" name="dob" value={formData.dob} onChange={handleChange} className="form-control" /></div>
                <div className="col-md-6"><label className="form-label">Work Status</label><input type="text" name="workStatus" value={formData.workStatus} onChange={handleChange} className="form-control" /></div>

                <h4 ref={el => sectionRefs.current[2] = el} data-section={2} className="form-section-title"><span className="section-number">2</span> Status & Dates</h4>
                <div className="col-md-6"><label className="form-label">Status</label><select name="status" value={formData.status} onChange={handleChange} className="form-select">{statusOptions.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
                <div className="col-md-6"><label className="form-label">Assigned To</label><select name="assignedTo" value={formData.assignedTo} onChange={handleChange} className="form-select"><option value="">Unassigned</option>{assignedToOptions.map(e => <option key={e} value={e}>{e}</option>)}</select></div>
                <div className="col-md-6"><label className="form-label">Due Date</label><input type="date" name="dueDate" value={formData.dueDate} onChange={handleChange} className="form-control" /></div>
                <div className="col-md-6"><label className="form-label">Received Date</label><input type="date" name="receivedDate" value={formData.receivedDate} onChange={handleChange} className="form-control" /></div>

                <h4 ref={el => sectionRefs.current[3] = el} data-section={3} className="form-section-title"><span className="section-number">3</span> Filing & Invoice</h4>
                <div className="col-md-6"><label className="form-label">Invoice Date</label><input type="date" name="invoiceDate" value={formData.invoiceDate} onChange={handleChange} className="form-control" /></div>
                <div className="col-md-6"><label className="form-label">Filing Date</label><input type="date" name="filingDate" value={formData.filingDate} onChange={handleChange} className="form-control" /></div>
                <div className="col-md-6"><label className="form-label">Draft Sent Date</label><input type="date" name="draftSentDate" value={formData.draftSentDate} onChange={handleChange} className="form-control" /></div>
                <div className="col-md-6"><label className="form-label">Invoice No.</label><input type="text" name="invoiceNo" value={formData.invoiceNo} onChange={handleChange} className="form-control" /></div>
                <div className="col-md-6"><label className="form-label">Invoice Amount</label><input type="text" name="invoiceAmount" value={formData.invoiceAmount} onChange={handleChange} className="form-control" /></div>
                <div className="col-md-6"><label className="form-label">Payment Received</label><input type="text" name="paymentReceived" value={formData.paymentReceived} onChange={handleChange} className="form-control" /></div>

                <h4 ref={el => sectionRefs.current[4] = el} data-section={4} className="form-section-title"><span className="section-number">4</span> Notes & History</h4>
                <div className="col-12"><label className="form-label">Notes</label><textarea name="notes" value={formData.notes} onChange={handleChange} className="form-control" rows="2"></textarea></div>
                <div className="col-12"><label className="form-label">Other Comments</label><textarea name="otherComments" value={formData.otherComments} onChange={handleChange} className="form-control" rows="2"></textarea></div>
                <div className="col-12"><label className="form-label">Family Details</label><textarea name="familyDetails" value={formData.familyDetails} onChange={handleChange} className="form-control" rows="2"></textarea></div>
                <div className="col-12"><label className="form-label">History</label><textarea name="history" value={formData.history} onChange={handleChange} className="form-control" rows="2"></textarea></div>
              </div>
            </div>
            <div className="drawer-footer">
              {isEditing && isAdmin && (
                <div className="position-relative me-auto">
                  <button type="button" className="btn btn-outline-danger rounded-pill px-4" onClick={handleDelete} disabled={saving}>
                    <FaTrash className="me-2" /> Delete
                  </button>
                  <ConfirmTooltip
                    show={showDeleteConfirm}
                    onConfirm={() => { onDelete(customer); setShowDeleteConfirm(false); }}
                    onCancel={() => setShowDeleteConfirm(false)}>
                    Are you sure you want to permanently delete this customer? This action cannot be undone.
                  </ConfirmTooltip>
                </div>
              )}
              <button type="button" className="btn btn-light border rounded-pill px-4" onClick={onClose}>Cancel</button>
              <button type="submit" form="personal-customer-form" className="btn btn-primary rounded-pill px-4" disabled={saving}>
                {saving ? 'Saving...' : (isEditing ? 'Save Changes' : 'Add Customer')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default CustomerDrawer;